import type { PaperUse } from "../api/types";

/**
 * G-121 S4 (OD-142/OD-143): what an uploaded supplier paper is for, in words
 * a person picks from. The rules themselves live on the server
 * (`src/modules/invoices/paper-rules.ts`); the two use sets are mirrored here
 * only to decide what to show — the server refuses anything else.
 */

export const PAPER_USES: readonly PaperUse[] = [
  "ALBARAN",
  "FACTURA",
  "FACTURA_WITH_GOODS",
  "CREDIT_NOTE",
  "ITEMS_ONLY",
];

/** Mirrors `SCHEDULING_USES` on the server. */
export const SCHEDULING_USES: readonly PaperUse[] = ["ALBARAN", "FACTURA_WITH_GOODS"];
/** Mirrors `BILL_USES` on the server. */
export const BILL_USES: readonly PaperUse[] = ["FACTURA", "FACTURA_WITH_GOODS"];

export function canSchedule(use: PaperUse): boolean {
  return SCHEDULING_USES.includes(use);
}

export function isBill(use: PaperUse): boolean {
  return BILL_USES.includes(use);
}

/** G-173: a credit note (abono) — mirrors `isCreditNotePaper` on the server. */
export function isCreditNote(use: PaperUse): boolean {
  return use === "CREDIT_NOTE";
}

const LABELS: Record<PaperUse, string> = {
  ALBARAN: "Albarán",
  FACTURA: "Factura",
  FACTURA_WITH_GOODS: "Factura with the goods",
  ITEMS_ONLY: "Items only",
  CREDIT_NOTE: "Credit note",
};

const SENTENCES: Record<PaperUse, string> = {
  ALBARAN: "Delivery note that comes with the goods. Schedules the delivery; its prices are the stock cost.",
  FACTURA: "The bill — what you pay and the gestor deducts IVA from. Never schedules a delivery.",
  FACTURA_WITH_GOODS: "A factura that comes with the goods: schedules the delivery and is the bill.",
  ITEMS_ONLY: "Uploaded only to create catalogue items. Counts for nothing: no stock, no money.",
  CREDIT_NOTE: "Abono: negative amounts that correct a factura. No stock.",
};

/**
 * OD-155 (owner, 2026-10-03): the use a supplier's paper is pre-selected as on
 * the upload page. Deco Spirit sends a factura WITH the goods, every time.
 * Only a pre-selection — the person can still pick another use; every other
 * supplier keeps OD-143's "no default, the person picks".
 */
const SUPPLIER_DEFAULT_USE: Readonly<Record<string, PaperUse>> = {
  DECO_SPIRIT_SL: "FACTURA_WITH_GOODS",
};

export function supplierDefaultPaperUse(supplierCode: string | null | undefined): PaperUse | null {
  return supplierCode ? (SUPPLIER_DEFAULT_USE[supplierCode] ?? null) : null;
}

export function paperUseLabel(use: PaperUse): string {
  return LABELS[use];
}

/**
 * G-164 (KI-112, OD-166): on a paper that already became a delivery, "Items
 * only" is the way out for an albarán whose factura will never come — the
 * delivery and its stock stay; the paper counts for nothing.
 */
const REFILED_ITEMS_ONLY_SENTENCE =
  "Its factura will never come. The delivery and its stock stay; this paper counts for nothing and waits for nothing.";

export function paperUseSentence(use: PaperUse, committed = false): string {
  return committed && use === "ITEMS_ONLY" ? REFILED_ITEMS_ONLY_SENTENCE : SENTENCES[use];
}

/** What the reader made of the paper, read defensively from `parsedPayload`. */
function readerDocumentKind(parsedPayload: unknown): string | null {
  if (typeof parsedPayload !== "object" || parsedPayload === null) return null;
  const kind = (parsedPayload as { documentKind?: unknown }).documentKind;
  return typeof kind === "string" ? kind : null;
}

/**
 * A warning when the reader read the paper as something else than the person
 * said. A hint only — the person decides; suppliers that the reader cannot
 * tell apart (Deco, Flore) never trigger it.
 */
export function readerDisagreement(use: PaperUse, parsedPayload: unknown): string | null {
  const kind = readerDocumentKind(parsedPayload);
  if (kind === "CONSOLIDATED_INVOICE" && canSchedule(use)) {
    return "The reader reads this paper as a factura listing several albaranes, not as a delivery paper.";
  }
  // OD-155: a factura that comes with the goods (Deco Spirit) is both jobs.
  if (kind === "INVOICE" && (use === "ALBARAN" || use === "FACTURA")) {
    return "The reader reads this paper as a factura that comes with the goods: it is the bill and it brings the stock. Choose Factura with the goods.";
  }
  if (kind === "DELIVERY_NOTE" && isBill(use)) {
    return "The reader reads this paper as an albarán (delivery note), not as a factura.";
  }
  // G-173: negative totals, or a title that says abono / rectificativa.
  if (kind === "CREDIT_NOTE" && !isCreditNote(use)) {
    return "The reader reads this paper as a credit note (abono). Choose Credit note.";
  }
  if (kind !== null && kind !== "CREDIT_NOTE" && isCreditNote(use)) {
    return "The reader does not read this paper as a credit note: its totals are not negative.";
  }
  return null;
}

/**
 * The uses this paper may be switched to. A paper that already became a
 * delivery stays a delivery paper (the server's lock) — except that an
 * albarán may be re-filed as "Items only" (G-164, OD-166; the server's
 * `isRefiledAlbaran`); every other change is offered and the server says no
 * when a factura still has payments or a link holds it.
 */
export function paperUseChoices(committed: boolean, current: PaperUse): readonly PaperUse[] {
  if (!committed) return PAPER_USES;
  return current === "ALBARAN" || current === "ITEMS_ONLY"
    ? [...SCHEDULING_USES, "ITEMS_ONLY"]
    : SCHEDULING_USES;
}
